// assembleState — pure transform from raw Supabase rows → the AppState slices
// the store owns. Extracted verbatim from SupabaseStore.fetchAll so the exact
// same assembly serves both:
//   - the authed realtime store (SupabaseStore.fetchAll), and
//   - the public edge-cache snapshot (the Worker runs the identical reads with
//     the anon key — RLS returns empty for the private tables, so the private
//     slices come out empty here without any special-casing).
//
// Keeping ONE assembly means the spectator snapshot and the operator view can
// never drift. The caller is responsible for the fail-safe (don't call this
// with hollowed-out core reads); this function trusts its inputs.

import type {
  AppState, Match, MatchEvent, MatchTeamStatus, ARCTICEntry, ECRun, PracticeMatch,
  QueueItem, TeamRobotInspection, RobotRollupStatus, CheckIn, Warning, WarningSeverity,
  PreInspectionLevel, MatchResults, RoundWinner, MatchNote, TeamNotes, RobotWeight,
  RobotInstance, FieldCalibration, TeamPictures, TeamConfig, GroupLetter,
  RefereeModules, RefereeModuleLog, RefereeOnLoan,
} from '../types';
import { eventOfMatch } from '../lib/match-event';
import { TEAMS } from '../mock/data';
import { canonicalEventForRobot } from '../state/preInspection';

// ---------------------------------------------------------------------------
// Raw row shapes (the columns each PostgREST select pulls). supabase-js without
// generated DB types hands back `any`, so the fetchAll call site assigns into
// these cleanly; the strict shapes here are what give the transform its safety.
// ---------------------------------------------------------------------------

export interface MatchRow {
  id: string; kind: string; scheduled_at: string | null;
  red_team: string | null; blue_team: string | null;
  presenter: string | null; topic: string | null; field: string | null;
  is_bye: boolean | null;
}
export interface MatchStateRow {
  match_id: string; status_red: string | null; status_blue: string | null; winner_side: string | null;
}
export interface EventStateRow {
  live_3v3_match_id?: string | null; on_deck_3v3_match_id?: string | null;
  live_1v1_match_id?: string | null; on_deck_1v1_match_id?: string | null;
  quals_3v3_live?: boolean | null; quals_1v1_live?: boolean | null;
  quals_3v3_begun?: boolean | null; quals_1v1_begun?: boolean | null;
  test_match_3v3?: boolean | null;
  official_delay_3v3_minutes?: number | null; official_delay_1v1_minutes?: number | null;
  official_delay_3v3_block_id?: string | null; official_delay_1v1_block_id?: string | null;
  [k: string]: unknown; // event_state is select('*')
}
interface QueueJoin { kind: string | null; scheduled_at: string | null }
export interface QueueRow {
  id: string; team_id: string; match_id: string | null; robot_class: string;
  position: number | null; busy_at_station: string | null; claimed_by: string | null; claimed_at?: string | null;
  is_backup: boolean | null; pre_inspection?: boolean | null; skipped: boolean | null; failed: boolean | null; urgent: boolean | null;
  s1_result: string | null; s2_result: string | null; s3_result: string | null;
  matches: QueueJoin | QueueJoin[] | null;
}
export interface WarningRow {
  id: string; match_id: string | null; team_id: string; code: string | null;
  severity: string; note: string | null; issued_at: string | null;
  issued_by: string | null; issued_by_email: string | null; issued_by_role: string | null;
}
export interface CheckInRow {
  team_id: string; spiel_given: boolean; badges: number;
  badges_1v1: boolean; badges_3v3: boolean; badges_ec: boolean | null;
  borrowing: boolean; check_in_form: boolean;
  points_penalty_3v3: number | null; points_penalty_ec: number | null; has_backup_robot: boolean | null;
}
export interface RobotRow {
  team_id: string; robot_class: string; station: string; event: string;
  result: 'pending' | 'active' | 'pass' | 'fail' | 'recheck';
  weight_kg?: number | null; marked_at?: string | null;
}
export interface PreInspRow { team_id: string; event: string; robot_class: string; level: string }
export interface RoundRow { match_id: string; round_number: number; winner: string; red_score: number; blue_score: number }
export interface NoteRow {
  id: string; match_id?: string; team_id?: string; body: string;
  author_email: string; author_role: string | null; created_at: string;
}
export interface TeamRow { id: string; group_3v3: string | null; initial_seed_1v1: number | null; practice_bonus: number | null }
export interface RefModuleRow {
  team_id: string; module_type: string;
  ref_checkout: number | null; exchange_extras: number | null; ref_return: number | null;
}
export interface RefOnLoanRow { team_id: string; module_type: string; on_loan: number | null }
export interface EcPenaltyRow { team_id: string; points_penalty_ec: number | null }
export interface Penalty3v3Row { team_id: string; points_penalty_3v3: number | null }
export interface FieldCalRow { team_id: string; event: string; done: boolean | null }
export interface PictureRow {
  team_id: string; event: string; robot_class: string;
  robot: boolean | null; driver: boolean | null; driver_name: string | null; driver_pronouns: string | null;
  chief: boolean | null; chief_name: string | null; chief_pronouns: string | null;
}
export interface RobotWeightRow {
  team_id: string; event: string; robot_class: string; instance: string | null;
  weight_kg: number | null; set_at: string | null;
}

export interface RawReads {
  matches: MatchRow[];
  matchStates: MatchStateRow[];
  eventState: EventStateRow | null;
  queue: QueueRow[];
  warnings: WarningRow[];
  checkIn: CheckInRow[];
  robots: RobotRow[];
  preInsp: PreInspRow[];
  rounds: RoundRow[];
  matchNotes: NoteRow[];
  teamNotes: NoteRow[];
  refereeModules: RefModuleRow[];
  refereeModuleLog: NoteRow[];
  refereeOnLoan: RefOnLoanRow[];
  teams: TeamRow[];
  ecPenalties: EcPenaltyRow[];
  // Public-readable 3v3 RP penalty (penalty_3v3 view). Carries the penalty to
  // spectators, who can't read the ops-only check_in table it mirrors.
  penalties3v3: Penalty3v3Row[];
  fieldCal: FieldCalRow[];
  pictures: PictureRow[];
  robotWeight: RobotWeightRow[];
}

// The slices this transform produces. Everything else in AppState (UI-local
// state like activeInspection / pendingWarning) is preserved by the caller's
// spread.
export type AssembledState = Pick<AppState,
  | 'matches3v3' | 'matches1v1' | 'arctic' | 'ec' | 'queue' | 'warnings' | 'checkIn'
  | 'fieldCalibration' | 'teamPictures' | 'teamRobotInspection' | 'preInspectionLevel'
  | 'matchResults' | 'matchNotes' | 'teamNotes' | 'refereeModules' | 'refereeModuleLog'
  | 'refereeOnLoan'
  | 'robotWeight' | 'officialDelay'
  | 'officialDelayBlock' | 'qualsLive' | 'qualsBegun' | 'testMatch3v3' | 'practiceMatches' | 'teamConfig' | 'ecPenalty'
>;

// A fully-zeroed check-in row. Used to fabricate penalty-only entries: public
// spectators never receive the real check_in row (RLS-blocked + stripped by
// toPublicSnapshot), only the 3v3 RP penalty merged in from the penalty_3v3
// view. Every other field reads as "not checked in", which is correct — the
// public has no business seeing check-in progress.
const EMPTY_CHECK_IN_ROW = {
  spielGiven: false, badges: 0, badges1v1: false, badges3v3: false, badgesEc: false,
  borrowing: false, checkInForm: false, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false,
};

export function assembleState(raw: RawReads): AssembledState {
  const matches = raw.matches;
  const matchStates = new Map(raw.matchStates.map((m) => [m.match_id, m]));
  const eventState = raw.eventState;
  // Per-event live/on-deck slots. 3v3 and 1v1 each track their own field
  // independently — advancing 1v1 must not corrupt 3v3 (and vice versa).
  const live3v3   = eventState?.live_3v3_match_id    ?? null;
  const onDeck3v3 = eventState?.on_deck_3v3_match_id ?? null;
  const live1v1   = eventState?.live_1v1_match_id    ?? null;
  const onDeck1v1 = eventState?.on_deck_1v1_match_id ?? null;
  // Captain's-Meeting commit flags. Default to false if the columns aren't
  // present (older migrations) so the admin screen lights up editable.
  const qualsLive3v3 = eventState?.quals_3v3_live === true;
  const qualsLive1v1 = eventState?.quals_1v1_live === true;
  // Quals-Begun flags. Deliberately `!== false`: when the columns don't exist
  // yet the read is undefined and we treat quals as BEGUN.
  const qualsBegun3v3 = eventState?.quals_3v3_begun !== false;
  const qualsBegun1v1 = eventState?.quals_1v1_begun !== false;
  const testMatch3v3 = eventState?.test_match_3v3 === true;

  // A match belongs to an event by its kind (qual_3v3 / qual_1v1) OR — for
  // playoff matches — by the id prefix the generate_bracket RPC stamps.
  const toAppMatch = (row: MatchRow): Match => {
    const ms = matchStates.get(row.id);
    const ev = eventOfMatch(row);
    const liveId   = ev === '3v3' ? live3v3   : ev === '1v1' ? live1v1   : null;
    const onDeckId = ev === '3v3' ? onDeck3v3 : ev === '1v1' ? onDeck1v1 : null;
    const isBye = row.kind === 'qual_1v1'
      && (row.is_bye === true || (row.red_team != null && row.blue_team == null));
    return {
      id: row.id,
      time: formatTime(row.scheduled_at ?? ''),
      scheduledAt: row.scheduled_at ?? undefined,
      red: row.red_team ?? '',
      blue: row.blue_team ?? '',
      redS: (ms?.status_red ?? 'not_tracked') as MatchTeamStatus,
      blueS: (ms?.status_blue ?? 'not_tracked') as MatchTeamStatus,
      live: row.id === liveId,
      onDeck: row.id === onDeckId,
      bye: isBye || undefined,
    };
  };

  const matches3v3 = matches.filter((m) => eventOfMatch(m) === '3v3').map(toAppMatch);
  const matches1v1 = matches.filter((m) => eventOfMatch(m) === '1v1').map(toAppMatch);

  const arctic: ARCTICEntry[] = matches
    .filter((m) => m.kind === 'arctic')
    .map((m) => ({
      id: m.id,
      time: formatTime(m.scheduled_at ?? ''),
      day: formatDay(m.scheduled_at ?? ''),
      topic: m.topic ?? '',
      presenter: m.presenter ?? '',
      team: m.red_team ?? '',
    }));

  const ec: ECRun[] = matches
    .filter((m) => m.kind === 'ec')
    .map((m) => {
      const ms = matchStates.get(m.id);
      const status = (ms?.status_red ?? 'not_tracked') as MatchTeamStatus;
      return {
        id: m.id,
        time: formatTime(m.scheduled_at ?? ''),
        day: formatDay(m.scheduled_at ?? ''),
        team: m.red_team ?? '',
        status,
        completed: status === 'completed' || status === 'forfeit_v' || status === 'forfeit_iv',
        live: false,
      };
    });

  // Practice matches — pre-quals scrimmages. eventOfMatch returns null for
  // practice kinds, so they never leak into matches3v3/matches1v1.
  const practiceMatches: PracticeMatch[] = matches
    .filter((m) => m.kind === 'practice_3v3' || m.kind === 'practice_1v1')
    .map((m) => {
      const ms = matchStates.get(m.id);
      return {
        id: m.id,
        event: (m.kind === 'practice_3v3' ? '3v3' : '1v1') as PracticeMatch['event'],
        time: formatTime(m.scheduled_at ?? ''),
        scheduledAt: m.scheduled_at ?? undefined,
        red: m.red_team ?? '',
        blue: m.blue_team ?? '',
        status: (ms?.status_red ?? 'not_tracked') as MatchTeamStatus,
        winnerSide: (ms?.winner_side ?? null) as PracticeMatch['winnerSide'],
      };
    });

  // Queue: derive event from joined match.kind; sort by referenced match time.
  const robotRows = raw.robots;
  const passedByRobot = computePassedStationsByRobot(robotRows);

  const queueRows = raw.queue.slice().sort((a, b) => {
    const aSkip = a.skipped ? 1 : 0;
    const bSkip = b.skipped ? 1 : 0;
    if (aSkip !== bSkip) return aSkip - bSkip;
    const aJoin = Array.isArray(a.matches) ? a.matches[0] : a.matches;
    const bJoin = Array.isArray(b.matches) ? b.matches[0] : b.matches;
    const aAt = aJoin?.scheduled_at ?? '';
    const bAt = bJoin?.scheduled_at ?? '';
    return aAt.localeCompare(bAt);
  });

  const queue: QueueItem[] = queueRows.map((row) => {
    const joined = Array.isArray(row.matches) ? row.matches[0] : row.matches;
    const kind = joined?.kind;
    // Event from the match: quals by kind, playoffs by the U_/E_ id prefix
    // (eventOfMatch — the join only selects kind, so use the row's match_id),
    // EC by kind. The queue now includes playoff matches (teams re-inspect
    // before every match), so a kind-only `else → 3v3` would mislabel 1v1
    // playoffs (E_*) and break their per-event rollup + robot lookups.
    const isBackup = !!row.is_backup;
    const preInspection = !!row.pre_inspection;
    // Match-bound rows resolve event from the match (quals by kind, playoffs
    // by the U_/E_ id prefix via eventOfMatch, EC by kind). Pre-inspection
    // rows have NO match, so they'd fall through to '3v3' and mislabel a
    // 1v1-only team's Standard as "3v3 Infantry" (and stash its weight under
    // '3v3') — derive their event from the team's canonical event instead,
    // matching how the reducer mints them (robotsForTeam → canonicalEventForRobot).
    const preTeam = preInspection ? TEAMS.find((t) => t.id === row.team_id) : undefined;
    const event: MatchEvent = preTeam
      ? canonicalEventForRobot(preTeam, row.robot_class)
      : (eventOfMatch({ id: row.match_id ?? '', kind: kind ?? '' }) ?? (kind === 'ec' ? 'ec' : '3v3'));
    // Backups AND pre-inspection rows carry their own per-station progress in
    // the row's s{1,2,3}_result columns (they don't feed the per-event rollup).
    const passed: ('s1' | 's2' | 's3')[] = (isBackup || preInspection)
      ? (['s1', 's2', 's3'] as const).filter((st) => row[`${st}_result` as 's1_result'] === 'pass')
      : (passedByRobot.get(`${row.team_id}::${event}::${row.robot_class}`) ?? []);
    return {
      id: row.id,
      team: row.team_id,
      event,
      robotClass: row.robot_class,
      match: row.match_id ?? '',
      time: formatTime(joined?.scheduled_at ?? ''),
      passedStations: passed,
      busyAtStation: (row.busy_at_station ?? null) as QueueItem['busyAtStation'],
      claimedBy: row.claimed_by ?? null,
      claimedAt: row.claimed_at ?? null,
      isBackup,
      preInspection,
      skipped: !!row.skipped,
      failed: !!row.failed,
      urgent: !!row.urgent,
    };
  });

  // Per-event rollup: group by (team, event, robot_class).
  const teamRobotInspection: TeamRobotInspection = {};
  const grouped = new Map<string, Map<MatchEvent, Map<string, RobotRow[]>>>();
  for (const r of robotRows) {
    const ev = r.event as MatchEvent;
    if (!grouped.has(r.team_id)) grouped.set(r.team_id, new Map());
    const evMap = grouped.get(r.team_id)!;
    if (!evMap.has(ev)) evMap.set(ev, new Map());
    const robotMap = evMap.get(ev)!;
    if (!robotMap.has(r.robot_class)) robotMap.set(r.robot_class, []);
    robotMap.get(r.robot_class)!.push(r);
  }
  for (const [team, evMap] of grouped) {
    teamRobotInspection[team] = {};
    for (const [ev, robotMap] of evMap) {
      const eventEntry: Record<string, RobotRollupStatus> = {};
      for (const [robot, stations] of robotMap) {
        eventEntry[robot] = rollupLevel(stations);
      }
      teamRobotInspection[team]![ev] = eventEntry;
    }
  }

  const checkIn: CheckIn = {};
  for (const row of raw.checkIn) {
    checkIn[row.team_id] = {
      spielGiven: row.spiel_given,
      badges: row.badges,
      badges1v1: row.badges_1v1,
      badges3v3: row.badges_3v3,
      badgesEc:  !!row.badges_ec,
      borrowing: row.borrowing,
      checkInForm: row.check_in_form,
      pointsPenalty3v3: row.points_penalty_3v3 ?? 0,
      pointsPenaltyEc: row.points_penalty_ec ?? 0,
      hasBackupRobot: !!row.has_backup_robot,
    };
  }
  // Merge the public-readable 3v3 RP penalty (penalty_3v3 view) into checkIn.
  // For ops the full check_in row already carries pointsPenalty3v3; for public
  // spectators check_in is RLS-blocked, so this is the ONLY path that surfaces
  // the penalty to the public standings (computeStandings3v3 reads it from the
  // checkIn slice). Fabricate a penalty-only row when no full row exists.
  for (const row of raw.penalties3v3) {
    const pen = row.points_penalty_3v3 ?? 0;
    const existing = checkIn[row.team_id];
    if (existing) existing.pointsPenalty3v3 = pen;
    else checkIn[row.team_id] = { ...EMPTY_CHECK_IN_ROW, pointsPenalty3v3: pen };
  }

  const warnings: Warning[] = raw.warnings.map((w) => ({
    id: w.id,
    match: w.match_id ?? '',
    team: w.team_id,
    code: w.code ?? '',
    severity: w.severity as WarningSeverity,
    note: w.note ?? undefined,
    at: relativeTime(w.issued_at ?? ''),
    issuedById: w.issued_by ?? undefined,
    issuedByEmail: w.issued_by_email ?? undefined,
    issuedByRole: (w.issued_by_role as 'staff' | 'ops' | null) ?? undefined,
  }));

  // Pre-inspection level — standalone, separate from teamRobotInspection.
  const preInspectionLevel: PreInspectionLevel = {};
  for (const r of raw.preInsp) {
    const ev = r.event as MatchEvent;
    if (!preInspectionLevel[r.team_id]) preInspectionLevel[r.team_id] = {};
    const teamRec = preInspectionLevel[r.team_id]!;
    if (!teamRec[ev]) teamRec[ev] = {};
    teamRec[ev]![r.robot_class] = r.level as RobotRollupStatus;
  }

  // Per-match round outcomes — drives standings + bracket progression.
  const matchResults: MatchResults = {};
  for (const r of raw.rounds) {
    if (!matchResults[r.match_id]) matchResults[r.match_id] = [];
    matchResults[r.match_id].push({
      winner: r.winner as RoundWinner,
      redScore: r.red_score,
      blueScore: r.blue_score,
    });
  }

  // Append-only chat notes per match. RLS gates reads to staff+; for anon
  // viewers raw.matchNotes is empty and matchNotes stays {}.
  const matchNotes: Record<string, MatchNote[]> = {};
  for (const n of raw.matchNotes) {
    const matchId = n.match_id!;
    if (!matchNotes[matchId]) matchNotes[matchId] = [];
    matchNotes[matchId].push({
      id: n.id,
      matchId,
      body: n.body,
      authorEmail: n.author_email,
      authorRole: (n.author_role as 'volunteer' | 'staff' | 'ops' | null) ?? null,
      createdAt: n.created_at,
    });
  }

  const teamNotes: TeamNotes = {};
  for (const n of raw.teamNotes) {
    const teamId = n.team_id!;
    if (!teamNotes[teamId]) teamNotes[teamId] = [];
    teamNotes[teamId].push({
      id: n.id,
      teamId,
      body: n.body,
      authorEmail: n.author_email,
      authorRole: (n.author_role as 'volunteer' | 'staff' | 'ops' | null) ?? null,
      createdAt: n.created_at,
    });
  }

  // Referee System: per-team module counts + the append-only change log.
  const refereeModules: RefereeModules = {};
  for (const r of raw.refereeModules) {
    (refereeModules[r.team_id] ??= {})[r.module_type] = {
      refCheckout: r.ref_checkout ?? 0,
      exchangeExtras: r.exchange_extras ?? 0,
      refReturn: r.ref_return ?? 0,
    };
  }
  const refereeModuleLog: RefereeModuleLog = {};
  for (const n of raw.refereeModuleLog) {
    const teamId = n.team_id!;
    (refereeModuleLog[teamId] ??= []).push({
      id: n.id,
      teamId,
      body: n.body,
      authorEmail: n.author_email,
      authorRole: (n.author_role as 'volunteer' | 'staff' | 'ops' | null) ?? null,
      createdAt: n.created_at,
    });
  }
  // Public on-loan projection (the view only returns positive on_loan rows).
  const refereeOnLoan: RefereeOnLoan = {};
  for (const r of raw.refereeOnLoan) {
    if ((r.on_loan ?? 0) > 0) (refereeOnLoan[r.team_id] ??= {})[r.module_type] = r.on_loan!;
  }

  // Per-(team, event, robot_class, instance) weight. Prefer the robot_weight
  // table; fall back to legacy s1-row weights when that table is absent.
  const robotWeight: RobotWeight = {};
  const setWeight = (team: string, ev: MatchEvent, robotClass: string, instance: RobotInstance, kg: number, at: string) => {
    if (!robotWeight[team]) robotWeight[team] = {};
    const teamMap = robotWeight[team]!;
    if (!teamMap[ev]) teamMap[ev] = {};
    if (!teamMap[ev]![robotClass]) teamMap[ev]![robotClass] = {};
    teamMap[ev]![robotClass]![instance] = { kg, at };
  };
  if (raw.robotWeight.length > 0) {
    for (const r of raw.robotWeight) {
      if (r.weight_kg == null) continue;
      setWeight(r.team_id, r.event as MatchEvent, r.robot_class, (r.instance as RobotInstance) ?? 'primary', Number(r.weight_kg), r.set_at ?? '');
    }
  } else {
    for (const r of robotRows) {
      if (r.station !== 's1' || r.weight_kg == null) continue;
      setWeight(r.team_id, r.event as MatchEvent, r.robot_class, 'primary', Number(r.weight_kg), r.marked_at ?? '');
    }
  }

  // Per-team Captain's-Meeting config (group + initial seed).
  const teamConfig: TeamConfig = {};
  for (const t of raw.teams) {
    const entry: { group?: GroupLetter; initialSeed1v1?: number; practiceBonus?: number } = {};
    if (t.group_3v3) entry.group = t.group_3v3 as GroupLetter;
    if (t.initial_seed_1v1 != null) entry.initialSeed1v1 = t.initial_seed_1v1;
    if (t.practice_bonus != null && t.practice_bonus > 0) entry.practiceBonus = t.practice_bonus;
    if (entry.group != null || entry.initialSeed1v1 != null || entry.practiceBonus != null) {
      teamConfig[t.id] = entry;
    }
  }

  // Ops-only field-calibration flags per (team, event).
  const fieldCalibration: FieldCalibration = {};
  for (const r of raw.fieldCal) {
    const ev = r.event as MatchEvent;
    if (!fieldCalibration[r.team_id]) fieldCalibration[r.team_id] = {};
    fieldCalibration[r.team_id]![ev] = !!r.done;
  }

  // Public team-picture tracking per (team, event, robot_class).
  const teamPictures: TeamPictures = {};
  for (const r of raw.pictures) {
    const ev = r.event as MatchEvent;
    if (!teamPictures[r.team_id]) teamPictures[r.team_id] = {};
    const teamRec = teamPictures[r.team_id]!;
    if (!teamRec[ev]) teamRec[ev] = {};
    teamRec[ev]![r.robot_class] = {
      robot: !!r.robot,
      driver: !!r.driver,
      driverName: r.driver_name ?? '',
      driverPronouns: r.driver_pronouns ?? '',
      chief: !!r.chief,
      chiefName: r.chief_name ?? '',
      chiefPronouns: r.chief_pronouns ?? '',
    };
  }

  return {
    matches3v3,
    matches1v1,
    arctic,
    ec,
    queue,
    warnings,
    checkIn,
    fieldCalibration,
    teamPictures,
    teamRobotInspection,
    preInspectionLevel,
    matchResults,
    matchNotes,
    teamNotes,
    refereeModules,
    refereeModuleLog,
    refereeOnLoan,
    robotWeight,
    officialDelay: {
      '3v3': eventState?.official_delay_3v3_minutes ?? 0,
      '1v1': eventState?.official_delay_1v1_minutes ?? 0,
      'ec':  0,
    },
    officialDelayBlock: {
      '3v3': eventState?.official_delay_3v3_block_id ?? null,
      '1v1': eventState?.official_delay_1v1_block_id ?? null,
      'ec':  null,
    },
    qualsLive: {
      '3v3': qualsLive3v3,
      '1v1': qualsLive1v1,
      'ec':  false,
    },
    qualsBegun: {
      '3v3': qualsBegun3v3,
      '1v1': qualsBegun1v1,
      'ec':  true,
    },
    testMatch3v3,
    practiceMatches,
    teamConfig,
    ecPenalty: Object.fromEntries(
      raw.ecPenalties.map((r) => [r.team_id, r.points_penalty_ec ?? 0]),
    ),
  };
}

// Project the full assembled state down to what a PUBLIC spectator should
// receive. The authed store uses the full assembleState; only the edge-cache
// snapshot is projected through this. Forcing these slices empty:
//   - trims the inspection queue to its PRE-INSPECTION rows — the public team
//     page reads each robot's pre-inspection level off the queue
//     (preInspectionLevelOf), so those rows must survive; the regular-
//     inspection rows (the bulk of the trimmable bytes) are dropped;
//   - keeps ops data (check-in, notes, field-cal) out of a publicly-cached
//     payload even if their RLS ever loosens (defence in depth — the cache is
//     shared). They're already empty for anon today, so this is a no-op now.
// Slices public screens DO read — schedules, standings inputs, results,
// bracket, pictures, team warnings, robot weights, the inspection rollup,
// pre-inspection levels — stay populated.
export function toPublicSnapshot(s: AssembledState): AssembledState {
  return {
    ...s,
    // Public team page derives each robot's pre-inspection level from the queue
    // (preInspectionLevelOf filters q.preInspection) — KEEP those rows; drop the
    // rest. Was `[]`, which blanked pre-inspection status for anon viewers while
    // ops (live, unstripped connection) still saw it.
    queue: s.queue.filter((q) => q.preInspection),
    // Keep ONLY the 3v3 RP penalty from each check-in row (every other field
    // zeroed) — public standings need pointsPenalty3v3, but no other check_in
    // data should ride in a publicly-cached payload (defence in depth if
    // check_in RLS ever loosens). EC's penalty travels its own ecPenalty slice.
    checkIn: Object.fromEntries(
      Object.entries(s.checkIn)
        .filter(([, ci]) => (ci.pointsPenalty3v3 ?? 0) > 0)
        .map(([team, ci]) => [team, { ...EMPTY_CHECK_IN_ROW, pointsPenalty3v3: ci.pointsPenalty3v3 }]),
    ),
    matchNotes: {},
    teamNotes: {},
    refereeModules: {},
    refereeModuleLog: {},
    fieldCalibration: {},
  };
}

// ---------------------------------------------------------------------------
// Helpers (moved verbatim from SupabaseStore).
// ---------------------------------------------------------------------------

// Wall-clock formatting via UTC accessors — scheduled_at strings are encoded as
// naive-UTC (the Z is just a parse marker), so the operator sees the same time
// regardless of laptop timezone. Tagged "ET" so remote viewers know the zone:
// all event times are Eastern. Mirrors formatScheduledAt in src/state/schedule.ts.
export function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = (h % 12) || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period} ET`;
}

// Wall-clock day label via UTC accessors (e.g. "Jul 1"). Mirrors
// formatScheduledDay in src/state/schedule.ts; ARCTIC spans Jul 1-3 so its
// rows carry the day alongside the time.
function formatDay(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
  return `${month} ${d.getUTCDate()}`;
}

export function relativeTime(iso: string): string {
  if (!iso) return 'just now';
  const dt = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(dt / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function rollupLevel(stations: RobotRow[]): RobotRollupStatus {
  const passes = stations.filter((s) => s.result === 'pass').length;
  const failed = stations.some((s) => s.result === 'fail');
  if (failed) return 'failed';
  if (passes >= 3) return 'l3';
  if (passes >= 2) return 'l2';
  if (passes >= 1) return 'l1';
  return 'pending';
}

// Map "teamId::event::robotClass" → stations this specific robot has passed.
function computePassedStationsByRobot(rows: RobotRow[]): Map<string, ('s1' | 's2' | 's3')[]> {
  const out = new Map<string, ('s1' | 's2' | 's3')[]>();
  for (const r of rows) {
    if (r.result !== 'pass') continue;
    if (r.station !== 's1' && r.station !== 's2' && r.station !== 's3') continue;
    const key = `${r.team_id}::${r.event}::${r.robot_class}`;
    if (!out.has(key)) out.set(key, []);
    out.get(key)!.push(r.station);
  }
  return out;
}
